import { useTranslation } from "react-i18next";
import { ArrowLeft as ArrowLeftIcon } from "lucide-react";
import { ErrorState } from "../../../components/common/ErrorState/index.js";
import { LoadingState } from "../../../components/common/LoadingState/index.js";
import { SimpleTooltip } from "../../../components/ui/tooltip/index.js";
import { InboxIconButton } from "../InboxIconButton.js";
import {
  CommentReplyComposer,
  type CommentReplySendOptions,
  type CommentSendPolicy,
} from "./CommentReplyComposer.js";
import { CommentThreadTimeline } from "./CommentThreadTimeline.js";
import { SocialPostContextCard } from "./SocialPostContextCard.js";
import type { CommentThreadView } from "./use-comment-thread.js";
import type { SocialPostView } from "./use-social-post.js";

export function CommentThreadPane({
  threadView,
  postView,
  scope,
  showMobileBack,
  onMobileBack,
  onRetry,
  onToggleScope,
  onSelectComment,
  onSendPublic,
  onSendPrivate,
  replyDisabled,
  sendPolicy,
  onSendPolicyChange,
  showDetailsTrigger,
  onOpenDetails,
}: {
  threadView: CommentThreadView;
  /** When a post is selected without a comment — show PostHero + select CTA. */
  postView?: SocialPostView;
  scope: "thread" | "all_post_comments";
  showMobileBack?: boolean;
  onMobileBack?: () => void;
  onRetry: () => void;
  onToggleScope: () => void;
  onSelectComment?: (commentId: string) => void;
  onSendPublic?: (
    content: string,
    options: CommentReplySendOptions,
  ) => Promise<boolean> | boolean;
  onSendPrivate?: (
    content: string,
    options: CommentReplySendOptions,
  ) => Promise<boolean> | boolean;
  replyDisabled?: boolean;
  sendPolicy: CommentSendPolicy;
  onSendPolicyChange: (policy: CommentSendPolicy) => void;
  showDetailsTrigger?: boolean;
  onOpenDetails?: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();

  if (threadView.kind === "idle") {
    return (
      <PostCentricIdle
        postView={postView}
        showMobileBack={showMobileBack}
        onMobileBack={onMobileBack}
        onRetryPost={onRetry}
      />
    );
  }

  if (threadView.kind === "loading") {
    return <LoadingState message={t("inbox.comments.thread.loading")} />;
  }

  if (threadView.kind === "error") {
    return (
      <div className="grid h-full place-items-center p-5">
        <ErrorState
          layout="panel"
          message={threadView.message}
          onRetry={onRetry}
          retryLabel={t("inbox.retry")}
        />
      </div>
    );
  }

  const { data } = threadView;

  return (
    <div
      className="flex h-full min-h-0 flex-col bg-canvas"
      aria-label={t("inbox.comments.threadLabel")}
    >
      <ThreadHeader
        title={t("inbox.comments.thread.workspaceTitle")}
        subtitle={t("inbox.comments.thread.subtitle", {
          count: data.totalPostCommentsCount,
        })}
        showMobileBack={showMobileBack}
        onMobileBack={onMobileBack}
        showDetailsTrigger={showDetailsTrigger}
        onOpenDetails={onOpenDetails}
      />

      <SocialPostContextCard post={data.post} />

      <CommentThreadTimeline
        parentComment={data.parentComment}
        selectedComment={data.selectedComment}
        threadReplies={data.threadReplies}
        totalPostCommentsCount={data.totalPostCommentsCount}
        scope={scope}
        onToggleScope={onToggleScope}
        onSelectComment={onSelectComment}
      />

      <CommentReplyComposer
        privateReplyEligibility={data.privateReplyEligibility}
        onSendPublic={onSendPublic}
        onSendPrivate={onSendPrivate}
        disabled={
          replyDisabled === true || data.selectedComment.lifecycleState === "DELETED_ON_PROVIDER"
        }
        sendPolicy={sendPolicy}
        onSendPolicyChange={onSendPolicyChange}
      />
    </div>
  );
}

function PostCentricIdle({
  postView,
  showMobileBack,
  onMobileBack,
  onRetryPost,
}: {
  postView?: SocialPostView;
  showMobileBack?: boolean;
  onMobileBack?: () => void;
  onRetryPost: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();

  if (postView?.kind === "loading") {
    return <LoadingState message={t("inbox.comments.post.loading")} />;
  }

  if (postView?.kind === "error") {
    return (
      <div className="grid h-full place-items-center p-5">
        <ErrorState
          layout="panel"
          message={postView.message}
          onRetry={onRetryPost}
          retryLabel={t("inbox.retry")}
        />
      </div>
    );
  }

  if (postView?.kind === "ready") {
    return (
      <div className="flex h-full min-h-0 flex-col bg-canvas">
        <ThreadHeader
          title={t("inbox.comments.post.contextTitle")}
          subtitle={t("inbox.comments.post.openCount", {
            open: postView.post.openCommentsCount,
            total: postView.post.totalCommentsCount,
          })}
          showMobileBack={showMobileBack}
          onMobileBack={onMobileBack}
        />
        <SocialPostContextCard post={postView.post} />
        <div className="grid min-h-0 flex-1 place-items-center p-6 text-center">
          <div className="max-w-[20rem]">
            <h2 className="m-0 text-[1.0625rem] font-semibold text-ink">
              {t("inbox.comments.thread.selectTitle")}
            </h2>
            <p className="m-0 mt-1.5 text-[0.84375rem] text-muted">
              {t("inbox.comments.thread.selectPostDescription")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="grid h-full min-h-0 place-items-center bg-canvas p-6 text-center">
      <div className="max-w-[20rem]">
        <h2 className="m-0 text-[1.0625rem] font-semibold text-ink">
          {t("inbox.comments.thread.selectTitle")}
        </h2>
        <p className="m-0 mt-1.5 text-[0.84375rem] text-muted">
          {t("inbox.comments.thread.selectDescription")}
        </p>
      </div>
    </div>
  );
}

function ThreadHeader({
  title,
  subtitle,
  showMobileBack,
  onMobileBack,
  showDetailsTrigger,
  onOpenDetails,
}: {
  title: string;
  subtitle: string;
  showMobileBack?: boolean;
  onMobileBack?: () => void;
  showDetailsTrigger?: boolean;
  onOpenDetails?: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <header className="flex min-h-[56px] shrink-0 items-center gap-1.5 border-b border-line/70 bg-surface px-2.5">
      {showMobileBack ? (
        <SimpleTooltip content={t("inbox.back")}>
          <InboxIconButton aria-label={t("inbox.back")} onClick={onMobileBack}>
            <ArrowLeftIcon size={18} className="rtl:rotate-180" aria-hidden="true" />
          </InboxIconButton>
        </SimpleTooltip>
      ) : null}
      <div className="min-w-0 flex-1">
        <h2 className="m-0 truncate text-[0.90625rem] font-semibold tracking-[-0.012em] text-ink rtl:tracking-normal">
          {title}
        </h2>
        <p className="m-0 truncate text-[0.75rem] text-muted">{subtitle}</p>
      </div>
      {showDetailsTrigger && onOpenDetails ? (
        <button
          type="button"
          className="rounded-lg px-2.5 py-1.5 text-[0.75rem] font-semibold text-primary hover:bg-brand-mint focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary"
          onClick={onOpenDetails}
        >
          {t("inbox.details.open")}
        </button>
      ) : null}
    </header>
  );
}
